import { join } from 'node:path';
import { globby } from 'globby';
import { relativeFolderName } from '@wix/cli-fs';
import {
  API_DIR,
  WEB_METHOD_DIR,
  ecomAdditionalFeesConfigSchema,
  ecomShippingRatesConfigSchema,
  ecomDiscountsTriggerConfigSchema,
  ecomValidationsConfigSchema,
  ecomPaymentSettingsConfigSchema,
  ecomGiftCardsProviderConfigSchema,
  ecomRecommendationsProviderConfigSchema,
  externalDatabaseProviderConfigSchema,
  premiumCustomChargesConfigSchema,
  seoKeywordsSuggestionsConfigSchema,
  bookingsPricingProviderConfigSchema,
  staffSortingProviderConfigSchema,
  EVENTS_DIR,
  getApiFolder,
  getEventsFolder,
  SERVICE_PLUGINS_DIR,
  PlatformType,
} from '@wix/cli-app-definitions';
import { match } from 'variant';
import { normalizeGlobbyPattern } from '../path.js';
import { DevCenterComponent, ServicePlugin } from '../dev-center-component.js';
import { deriveServicePluginType, loadServicePluginConfig } from './config.js';

export const API_PATTERN = join(API_DIR, '*', 'api.{ts,tsx,js,jsx}');

export const WEB_METHOD_PATTERN = join(WEB_METHOD_DIR, '**', '*.web.ts');

export const EVENTS_PATTERN = join(EVENTS_DIR, '*', 'event.{ts,tsx,js,jsx}');

export const SERVICE_PLUGINS_PATTERN = join(
  SERVICE_PLUGINS_DIR,
  '*',
  '*',
  'plugin.{ts,tsx,js,jsx}'
);

async function loadBackendApi(
  projectFolder: string,
  apiPath: string
): Promise<DevCenterComponent<'Api'>> {
  const title = relativeFolderName(getApiFolder(projectFolder), apiPath);
  return DevCenterComponent.Api({
    title,
    relativePath: apiPath,
    platforms: [],
  });
}

export async function loadBackendApis(
  projectFolder: string
): Promise<Array<DevCenterComponent<'Api'>>> {
  const backendApiPaths = await globby(normalizeGlobbyPattern(API_PATTERN), {
    cwd: projectFolder,
  });

  const apis = await Promise.all(
    backendApiPaths.map((apiPath) => loadBackendApi(projectFolder, apiPath))
  );

  return apis;
}

async function loadBackendWebMethod(
  webMethodPath: string
): Promise<DevCenterComponent<'WebMethod'>> {
  return DevCenterComponent.WebMethod({
    relativePath: webMethodPath,
    platforms: [],
  });
}

export async function loadBackendWebMethods(
  projectFolder: string
): Promise<Array<DevCenterComponent<'WebMethod'>>> {
  const backendWebMethodsPaths = await globby(
    normalizeGlobbyPattern(WEB_METHOD_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const webMethods = await Promise.all(
    backendWebMethodsPaths.map((webMethodPath) =>
      loadBackendWebMethod(webMethodPath)
    )
  );

  return webMethods;
}

async function loadBackendEvent(
  projectFolder: string,
  eventPath: string
): Promise<DevCenterComponent<'Event'>> {
  return DevCenterComponent.Event({
    title: relativeFolderName(getEventsFolder(projectFolder), eventPath),
    relativePath: eventPath,
    platforms: [],
  });
}

export async function loadBackendEvents(
  projectFolder: string
): Promise<Array<DevCenterComponent<'Event'>>> {
  const backendEventsPaths = await globby(
    normalizeGlobbyPattern(EVENTS_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const events = await Promise.all(
    backendEventsPaths.map((eventPath) =>
      loadBackendEvent(projectFolder, eventPath)
    )
  );

  return events;
}

async function loadServicePlugin(
  pluginPath: string
): Promise<DevCenterComponent<'ServicePlugin'>> {
  const servicePluginType = deriveServicePluginType(pluginPath);

  return DevCenterComponent.ServicePlugin({
    platforms: [
      PlatformType.Site(),
      PlatformType.Editor(),
      PlatformType.Dashboard(),
    ],
    relativePath: pluginPath,
    servicePlugin: await match(servicePluginType, {
      STAFF_SORTING_PROVIDER: async () =>
        ServicePlugin.StaffSortingProvider(
          await loadServicePluginConfig(
            pluginPath,
            staffSortingProviderConfigSchema
          )
        ),
      BOOKINGS_PRICING_PROVIDER: async () =>
        ServicePlugin.BookingsPricingProvider(
          await loadServicePluginConfig(
            pluginPath,
            bookingsPricingProviderConfigSchema
          )
        ),
      ECOM_ADDITIONAL_FEES: async () =>
        ServicePlugin.EcomAdditionalFees(
          await loadServicePluginConfig(
            pluginPath,
            ecomAdditionalFeesConfigSchema
          )
        ),
      ECOM_SHIPPING_RATES: async () =>
        ServicePlugin.EcomShippingRates(
          await loadServicePluginConfig(
            pluginPath,
            ecomShippingRatesConfigSchema
          )
        ),
      ECOM_DISCOUNTS_TRIGGER: async () =>
        ServicePlugin.EcomDiscountsTrigger(
          await loadServicePluginConfig(
            pluginPath,
            ecomDiscountsTriggerConfigSchema
          )
        ),
      ECOM_VALIDATIONS: async () =>
        ServicePlugin.EcomValidations(
          await loadServicePluginConfig(pluginPath, ecomValidationsConfigSchema)
        ),
      ECOM_PAYMENT_SETTINGS: async () =>
        ServicePlugin.EcomPaymentSettings(
          await loadServicePluginConfig(
            pluginPath,
            ecomPaymentSettingsConfigSchema
          )
        ),
      ECOM_RECOMMENDATIONS_PROVIDER: async () =>
        ServicePlugin.EcomRecommendationsProvider(
          await loadServicePluginConfig(
            pluginPath,
            ecomRecommendationsProviderConfigSchema
          )
        ),
      EXTERNAL_DATABASE_PROVIDER: async () =>
        ServicePlugin.ExternalDatabaseProvider(
          await loadServicePluginConfig(
            pluginPath,
            externalDatabaseProviderConfigSchema
          )
        ),
      SEO_KEYWORDS_SUGGESTIONS: async () =>
        ServicePlugin.SeoKeywordsSuggestion(
          await loadServicePluginConfig(
            pluginPath,
            seoKeywordsSuggestionsConfigSchema
          )
        ),
      PREMIUM_CUSTOM_CHARGES: async () =>
        ServicePlugin.PremiumCustomCharges(
          await loadServicePluginConfig(
            pluginPath,
            premiumCustomChargesConfigSchema
          )
        ),
      GIFT_CARDS_PROVIDER: async () =>
        ServicePlugin.EcomGiftCardsProvider(
          await loadServicePluginConfig(
            pluginPath,
            ecomGiftCardsProviderConfigSchema
          )
        ),
    }),
  });
}

export async function loadServicePlugins(
  projectFolder: string
): Promise<Array<DevCenterComponent<'ServicePlugin'>>> {
  const backendPluginPaths = await globby(
    normalizeGlobbyPattern(SERVICE_PLUGINS_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const plugins = await Promise.all(
    backendPluginPaths.map((pluginPath) => loadServicePlugin(pluginPath))
  );

  return plugins;
}
